/* eslint-disable import/no-cycle */
import { call, all, put, takeLatest, takeLeading, select } from 'redux-saga/effects';

import {
    getUserConfigSuccess,
    getUserConfigFailed,
    createUserConfigSuccess,
    createUserConfigFailed,
    updateUserConfigSuccess,
    updateUserConfigFailed,
    getGlobalConfig,
    getGlobalConfigSuccess,
    getGlobalConfigFail,
    setUserCurrentLocation,
    updateGlobalConfigurationSuccess,
    updateGlobalConfigurationFail,
    fetchCampaignHostLtdPromotionAdminSuccess,
    fetchCampaignHostLtdPromotionAdminFail,
    patchCampaignHostLtdPromotionAdminSuccess,
    patchCampaignHostLtdPromotionAdminFail,
} from './action';
import { IConfigurationInitialState, types, IGlobalConfigurationUpdate } from './types';
import { api } from '../../configurations/api';
import {
    userConfigurationUrl,
    globalConfigUrl,
    globalConfigurationByIdUrl,
    campaignHostLtdPromotionAdminUrl,
} from '../../configurations/api/url';
import type {
    ICampaignHostLtdPromotionAdminResponse,
    ILtdPromotionAdminPatch,
} from '../../types/global/campaignHostAdmin.types';
import { requireApiPayload } from '../../utils/campaignHostSubscriptionApi';
import { IGlobalConfiguration } from '../../constants/types';
import { httpRequest } from '../types';
import { IGenericDocumentModifier } from '../../types/global/helper';
import { RootState } from '../root-reducer';
import { effectiveOperatingCountryCodeSelector } from '../../selectors/selectors';
import { SentryCapture } from '../../analytics/Sentry';
import { showToastMessage } from '../../utils/AppUtils';

export function* getGlobalConfiguration({ payload }: any): any {
    try {
        const { country: countryFromPayload } = payload || {};
        const effectiveCountry: string | undefined = yield select(effectiveOperatingCountryCodeSelector);
        const country = countryFromPayload ?? effectiveCountry;
        const url = country ? `${globalConfigUrl}&country=${country}` : globalConfigUrl;
        const response: IGlobalConfiguration = yield call(api, url, httpRequest.GET, null, 2);
        const { data, location } = response.data || {};
        yield put(getGlobalConfigSuccess(data));
        if (location) {
            yield put(setUserCurrentLocation(location));
        }
    } catch (error) {
        SentryCapture(error, 'error');
        yield put(getGlobalConfigFail(error));
    }
}

export function* getUserConfiguration({ resolve, reject }: any): any {
    try {
        const requestUrl = `${userConfigurationUrl}/get-my-config`;
        const response = yield call(api, requestUrl, httpRequest.GET, null, 2);
        const { data } = response;
        yield put(getUserConfigSuccess(data));
        if (resolve) {
            resolve(data);
        }
    } catch (error) {
        SentryCapture(error, 'error');
        yield put(getUserConfigFailed(error));
        if (reject) {
            reject(error);
        }
    }
}

function* createUserConfig({ payload, resolve, reject }: any): any {
    try {
        const response = yield call(api, userConfigurationUrl, httpRequest.POST, payload, 1, 0);
        const { data } = response;
        yield put(createUserConfigSuccess(data));
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        const { message } = error.data;
        reject(message);
        yield put(createUserConfigFailed(message));
    }
}

/**
 *
 * @param String `Configuration Id`
 */
function* updateUserConfiguration({ payload }: { payload: IGenericDocumentModifier }): any {
    const state: RootState = yield select();
    const { configuration }: { configuration: IConfigurationInitialState } = state;
    const { userSpecificConfiguration } = configuration;
    const configId = userSpecificConfiguration?._id;
    if (!configId) {
        SentryCapture(new Error('updateUserConfig: missing userSpecificConfiguration._id'), 'error');
        yield put(updateUserConfigFailed('Missing configuration id'));
        return;
    }
    try {
        const request = `${userConfigurationUrl}/${configId}`;
        const response = yield call(api, request, httpRequest.PATCH, payload.payload, 1, 0);
        // API shape: { status, data: { data: config } } or { status, data: config }
        const updated = response?.data?.data ?? response?.data ?? response;
        yield put(
            updateUserConfigSuccess({
                ...userSpecificConfiguration,
                ...(updated && typeof updated === 'object' ? updated : {}),
                ...(payload.payload && typeof payload.payload === 'object' ? payload.payload : {}),
            }),
        );
    } catch (error) {
        SentryCapture(error, 'error');
        yield put(updateUserConfigFailed(error));
    }
}

export function* updateGlobalConfiguration({
    payload,
}: {
    payload: {
        country?: string;
        steps: Array<{
            config: IGlobalConfigurationUpdate & { apply_to_all?: boolean };
            applyToAll?: boolean;
        }>;
    };
}): any {
    try {
        const { country, steps } = payload;
        let mergedUpdate: Record<string, unknown> = {};

        const hadApplyToAll = steps.some(s => s.applyToAll);

        for (const step of steps) {
            const query = new URLSearchParams({ ring: 'ring0' });
            if (country) {
                query.set('country', country);
            }
            if (step.applyToAll) {
                query.set('apply_to_all', 'true');
            }

            const url = `${globalConfigurationByIdUrl}?${query.toString()}`;
            const response = yield call(api, url, httpRequest.PATCH, step.config, 1, 0);
            const inner = response?.data ?? response ?? {};

            if (step.applyToAll && inner.apply_to_all) {
                const count = inner.updated_count ?? inner.matched_count;
                yield call(
                    showToastMessage,
                    count != null ? `Changes applied to ${count} countries` : 'Global override saved',
                    'success',
                );
                if (inner.applied_fields) {
                    mergedUpdate = { ...mergedUpdate, ...inner.applied_fields };
                }
            } else {
                const updated = inner.data ?? inner;
                if (updated && typeof updated === 'object') {
                    mergedUpdate = { ...mergedUpdate, ...updated };
                }
            }
        }

        if (Object.keys(mergedUpdate).length > 0) {
            yield put(updateGlobalConfigurationSuccess(mergedUpdate));
        }

        if (hadApplyToAll && country) {
            yield put(getGlobalConfig(country));
        } else if (!hadApplyToAll) {
            yield call(showToastMessage, 'Update successfull', 'success');
        }

        return { mergedUpdate, hadApplyToAll };
    } catch (error) {
        SentryCapture(error, 'error');
        yield put(updateGlobalConfigurationFail(error));
        throw error;
    }
}

interface TaskAction extends IGenericDocumentModifier {
    type: string;
    payload: any;
}

function* updateUserConfigWatcher() {
    yield takeLatest<TaskAction>(types.UPDATE_USER_CONFIG, updateUserConfiguration);
}

function* getUserConfigWatcher() {
    yield takeLatest(types.GET_CONFIGURATION, getUserConfiguration);
}

function* createUserConfigWatcher() {
    yield takeLatest(types.CREATE_CONFIGURATION, createUserConfig);
}

function* getGlobalConfigurationWatcher() {
    yield takeLeading(types.GET_GLOBAL_CONFIGURATION, getGlobalConfiguration);
}

function* updateGlobalConfigurationWatcher() {
    yield takeLatest<any>(types.UPDATE_GLOBAL_CONFIGURATION, updateGlobalConfiguration);
}

function* fetchCampaignHostLtdPromotionAdminSaga(): Generator<any, void, any> {
    try {
        const response = yield call(api, campaignHostLtdPromotionAdminUrl, httpRequest.GET, null, 2);
        const data = requireApiPayload<ICampaignHostLtdPromotionAdminResponse>(response);
        yield put(fetchCampaignHostLtdPromotionAdminSuccess(data));
    } catch (error) {
        SentryCapture(error, 'error');
        yield put(fetchCampaignHostLtdPromotionAdminFail(error));
    }
}

function* patchCampaignHostLtdPromotionAdminSaga({
    payload,
}: {
    payload: ILtdPromotionAdminPatch;
}): Generator<any, void, any> {
    try {
        const response = yield call(api, campaignHostLtdPromotionAdminUrl, httpRequest.PATCH, payload, 2);
        const data = requireApiPayload<ICampaignHostLtdPromotionAdminResponse>(response);
        yield put(patchCampaignHostLtdPromotionAdminSuccess(data));
        yield call(showToastMessage, 'Campaign host LTD settings saved', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        const message = error?.data?.message ?? error?.message ?? 'Unable to save LTD promotion settings';
        yield call(showToastMessage, message, 'error');
        yield put(patchCampaignHostLtdPromotionAdminFail(error));
    }
}

function* fetchCampaignHostLtdPromotionAdminWatcher() {
    yield takeLatest<any>(types.FETCH_CAMPAIGN_HOST_LTD_ADMIN, fetchCampaignHostLtdPromotionAdminSaga);
}

function* patchCampaignHostLtdPromotionAdminWatcher() {
    yield takeLatest<any>(types.PATCH_CAMPAIGN_HOST_LTD_ADMIN, patchCampaignHostLtdPromotionAdminSaga);
}

export default function* configurationSaga() {
    yield all([
        getUserConfigWatcher(),
        createUserConfigWatcher(),
        updateUserConfigWatcher(),
        getGlobalConfigurationWatcher(),
        updateGlobalConfigurationWatcher(),
        fetchCampaignHostLtdPromotionAdminWatcher(),
        patchCampaignHostLtdPromotionAdminWatcher(),
    ]);
}
